import LoadingButton from '@/common/components/base/button/loadingButton.tsx';
import DateInput from '@/common/components/base/input/dateInput/DateInput.tsx';
import TextInputWithSelectOnMap from '@/common/components/base/input/mapInput/TextInputWithSelectOnMap';
import ModalInput from '@/common/components/base/input/modalInput/ModalInput.tsx';
import TimeInput from '@/common/components/base/input/timeInput/TimeInput.tsx';
import TitleDescription from '@/common/components/base/typography/TitleDescription.tsx';
import MainLayout from '@/common/components/layouts/MainLayout.tsx';
import LoadingPage from '@/common/pages/LoadingPage.tsx';
import { SelectionOptionsType } from '@/common/types/SelectionOptions.type';
import { convertPersianToGregorian } from '@/common/utils/convertPersianToGregorian.ts';
import IncidentDetailMainDetailCard from '@/modules/adjusterReportModules/IncidentDetails/components/incidentDetails/incidentDetailMainDetailCard';
import useIncidentDetailsProperty from '@/modules/adjusterReportModules/IncidentDetails/pages/propertyDamageDetails/propertyDamageDetailsPage.biz';
import { IncidentDetailsPropertyFormType } from '@/modules/adjusterReportModules/IncidentDetails/pages/propertyDamageDetails/propertyDamageDetailsPage.types';
import { validateNotFutureDate } from '@/modules/adjusterReportModules/IncidentDetails/utils/incidentDetails.util';
import { useState } from 'react';
import {
  FormProvider,
  SubmitHandler,
  useForm,
  useFormContext,
} from 'react-hook-form';
import { css } from 'styled-system/css';
import { Box, Stack } from 'styled-system/jsx';

export const PropertyDamageDetails = () => {
  const methods = useForm({ mode: 'onChange' });
  return (
    <MainLayout header="titleHeader" title="مشخصات حادثه" hasBottomPadding>
      <FormProvider {...methods}>
        <PropertyDamageDetailsPage />
      </FormProvider>
    </MainLayout>
  );
};

const PropertyDamageDetailsPage = () => {
  const [filteredIncidentCauseData, setFilteredIncidentCauseData] = useState<
    SelectionOptionsType[] | undefined
  >(undefined);
  const { handleSubmit, formState } =
    useFormContext<IncidentDetailsPropertyFormType>();
  const {
    incidentCardInfoPending,
    incidentCardInfo,
    incidentCauseData,
    incidentCausePending,
    formMutate,
    mutatePending,
  } = useIncidentDetailsProperty();

  const incidentFilterHandler = (value: string) => {
    if (!value) setFilteredIncidentCauseData(incidentCauseData);
    else
      setFilteredIncidentCauseData(() => {
        return (
          incidentCauseData?.filter((item: any) =>
            item?.label?.includes(value),
          ) || []
        );
      });
  };

  const onSubmit: SubmitHandler<IncidentDetailsPropertyFormType> = (data) => {
    const mutateData = {
      event_cause: data.event_cause.value,
      event_happening_date: convertPersianToGregorian(
        data.event_happening_date,
      ),
      event_happening_time: data.event_happening_time,
      event_happening_site: data.event_pos,
      event_lat: data.event_lat ?? undefined,
      event_lng: data.event_lng ?? undefined,
      event_happening_description: data.event_happening_description,
    };
    formMutate(mutateData);
  };

  if (incidentCardInfoPending) return <LoadingPage />;

  return (
    <Stack>
      <Box className={css({ marginTop: 25 })}>
        <IncidentDetailMainDetailCard
          fileNumber={incidentCardInfo?.order_id!}
          fileType={incidentCardInfo?.file_type.const!}
          svgColor="colors.pink"
          items={(incidentCardInfo?.detailCardItems ?? [])?.filter(
            (item) => item?.keyName !== 'محل وقوع حادثه',
          )}
          hasBullet={true}
          backgroundColor="pink-1"
          headerClassName={css({ borderColor: 'pink-1' })}
        />
      </Box>
      <TitleDescription my={'20px'}>
        فرم زیر را تکمیل نمایید :{' '}
      </TitleDescription>
      <form
        onSubmit={handleSubmit(onSubmit)}
        className={css({
          gap: '32px',
          display: 'flex',
          flexDirection: 'column',
        })}
      >
        <ModalInput
          rules={{
            required: 'وارد کردن علت حادثه اجباری است',
          }}
          searchOptions={{
            label: 'جستجو',
            onChange: (value: any) => incidentFilterHandler(value),
          }}
          options={
            filteredIncidentCauseData
              ? filteredIncidentCauseData
              : incidentCauseData
          }
          loading={incidentCausePending}
          name="event_cause"
          label="علت حادثه"
          placeholder="انتخاب کنید"
        />
        <DateInput
          label="تاریخ وقوع حادثه"
          rules={{
            required: 'وارد کردن تاریخ وقوع حادثه الزامی است',
            validate: validateNotFutureDate,
          }}
          buttonText="تاریخ وقوع حادثه"
          modalLabel="تاریخ وقوع حادثه"
          name="event_happening_date"
        />
        <TimeInput
          label="ساعت وقوع حادثه"
          rules={{ required: 'وارد کردن ساعت وقوع حادثه الزامی است' }}
          buttonText="ثبت ساعت بازدید"
          modalLabel="ساعت وقوع حادثه"
          name="event_happening_time"
        />

        <TextInputWithSelectOnMap
          rules={{ required: 'وارد کردن محل وقوع حادثه اجباری است' }}
          label="محل وقوع"
          placeholder="انتخاب محل وقوع"
          entranceModalText="در ادامه آدرس محل وقوع حادثه را بر روی نقشه مشخص کنید ."
          mapModalTitle="محل وقوع"
          name="event_pos"
          phiName="event_lat"
          lambdaName="event_lng"
        />

        <LoadingButton
          disabled={!formState?.isValid}
          fullWidth
          loading={mutatePending}
        >
          ثبت
        </LoadingButton>
      </form>
    </Stack>
  );
};
